// src/components/page/home/ScheduleTableDisplay.jsx
import React, { useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { formatDate } from '@/lib/dateUtils';
import { getGroupModeForDate } from '@/lib/groupModeUtils';
import LoaderWrapper from '@/components/page/home/LoaderWrapper';

const ScheduleTableDisplay = ({
  schedules,
  viewMode,
  ViewModes,
  teacherViewScope,
  TeacherViewScopes,
  groupModes,
  selectedDate
}) => {
  const rows = Array.isArray(schedules) ? schedules : [];
  const safeGroupModes = Array.isArray(groupModes) ? groupModes : [];
  const isLoading = !Array.isArray(schedules);

  const safeText = (v) => (v === null || v === undefined ? '' : String(v));

  const safeGetModeIcon = (gm, groupName, date) => {
    try {
      const mode = getGroupModeForDate(gm, groupName || '', date || '');
      if (mode === 'auditory') return '🏫';
      if (mode === 'remote') return '💻';
      return null;
    } catch {
      return null;
    }
  };
// 🔤 Українські назви днів тижня
  const ukDays = ['Неділя','Понеділок','Вівторок','Середа','Четвер','Пʼятниця','Субота'];
  const dayNameFromYMD = (ymd) => {
    if (!ymd || typeof ymd !== 'string') return '';
    const [y, m, d] = ymd.split('-').map(Number);
    if (!y || !m || !d) return '';
    const dt = new Date(y, (m || 1) - 1, d || 1);
    const idx = dt.getDay(); // 0..6
    return ukDays[idx] || '';
  };

  // ✅ Парсер ISO-рядка 'YYYY-MM-DD' у локальну дату
  const parseYMDToDate = (ymd) => {
    const [y, m, d] = (ymd || '').split('-').map(Number);
    return (y && m && d) ? new Date(y, m - 1, d) : null;
  };


  // Ключ сортування за датою (yyyy-MM-dd)
 const byDateKey = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // Відсортований список
  const sorted = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      const ad = byDateKey(a?.date);
      const bd = byDateKey(b?.date);
      if (ad !== bd) return ad.localeCompare(bd);
      return String(a?.time_slot || '').localeCompare(String(b?.time_slot || ''));
    });
    return arr;
  }, [rows]);

  // Кількість колонок для заголовка-дня у WEEK
  const columnsCount = useMemo(() => {
if (viewMode === ViewModes.BY_TEACHER && teacherViewScope === TeacherViewScopes.WEEK) {
      // У вкладці "по викладачу" колонки: Час, Група, Предмет, Аудиторія
      return 4;
    }
    // Для інших режимів лишаємо поточну ширину
    return 5;
  }, [viewMode, teacherViewScope, ViewModes, TeacherViewScopes]);

  // Побудова рядків для рендера (поза JSX — простіше і без синтаксичних пасток)
  const rowsForRender = useMemo(() => {
    // Режим ВИКЛАДАЧ -> ТИЖДЕНЬ: групуємо за датами, вставляємо "розділювач-день"
    if (viewMode === ViewModes.BY_TEACHER && teacherViewScope === TeacherViewScopes.WEEK) {
      const buckets = {};
      for (const r of sorted) {
        const k = byDateKey(r?.date);
        if (!k) continue;
        if (!buckets[k]) buckets[k] = [];
        buckets[k].push(r);
      }

      const days = Object.keys(buckets).sort();
      const out = [];
      let idx = 0;

      for (const dayKey of days) {
        // Рядок-заголовок дня
        out.push(
          <TableRow key={`hdr-${dayKey}`} className="bg-muted/30">
            <TableCell colSpan={columnsCount} className="font-semibold">
        {`${dayNameFromYMD(dayKey)} • ${formatDate(parseYMDToDate(dayKey), 'd MMMM yyyy')}`}
            </TableCell>
          </TableRow>
        );

        // Рядки з парами
        for (const entry of buckets[dayKey]) {
          const idKey =
            entry?.id ||
            `${idx++}-${safeText(entry?.group_name)}-${safeText(entry?.time_slot)}-${safeText(entry?.date)}`;
          const icon = safeGetModeIcon(safeGroupModes, entry?.group_name, entry?.date);

          out.push(
            <TableRow key={idKey}>
{/* Без колонки "Дата" у вкладці викладача */}
              <TableCell>{safeText(entry?.time_slot)}</TableCell>
              <TableCell>
                {safeText(entry?.group_name)}
                {icon ? <span className="ml-2">{icon}</span> : null}
              </TableCell>
              <TableCell>{safeText(entry?.subject)}</TableCell>
              <TableCell>{safeText(entry?.room)}</TableCell>
            </TableRow>
          );
        }
      }

      return out;
    }

    // Інші режими (включно з BY_TEACHER DAY/MONTH/YEAR) — стандартне відображення
     return sorted.map((entry, index) => {
            const idKey =
              entry?.id ||
              `${index}-${safeText(entry?.group_name)}-${safeText(entry?.time_slot)}-${safeText(entry?.date)}`;

            const icon = safeGetModeIcon(safeGroupModes, entry?.group_name, entry?.date);

            const displayDate =
              viewMode === ViewModes.BY_TEACHER &&
              (teacherViewScope === TeacherViewScopes.DAY ||
               teacherViewScope === TeacherViewScopes.MONTH ||
               teacherViewScope === TeacherViewScopes.YEAR)
                ? (entry?.date ? formatDate(new Date(entry.date), 'yyyy-MM-dd') : '')
                : null;

            return (
              <TableRow key={idKey} className={index % 2 === 0 ? 'bg-muted/20' : ''}>
                {viewMode === ViewModes.BY_TEACHER && teacherViewScope === TeacherViewScopes.YEAR && (
                  <TableCell>{index + 1}</TableCell>
                )}
                {displayDate !== null && <TableCell>{displayDate}</TableCell>}
                <TableCell>{safeText(entry?.time_slot)}</TableCell>
                {viewMode !== ViewModes.BY_GROUP && (
                  <TableCell>
                    {safeText(entry?.group_name)}
                    {icon ? <span className="ml-2">{icon}</span> : null}
                  </TableCell>
                )}
                <TableCell>{safeText(entry?.subject)}</TableCell>
                {viewMode !== ViewModes.BY_TEACHER && <TableCell>{safeText(entry?.teacher)}</TableCell>}
                <TableCell>{safeText(entry?.room)}</TableCell>
              </TableRow>
            );
          });
  }, [
    sorted,
    viewMode,
    teacherViewScope,
    ViewModes,
    TeacherViewScopes,
    safeGroupModes,
  ]);

  return (
    <div className="overflow-x-auto rounded-lg border border-border/50 min-h-[150px] relative">
      <LoaderWrapper loading={isLoading}>
        <Table>
          <TableHeader className="bg-muted/10">
<TableRow>
            {viewMode === ViewModes.BY_TEACHER && teacherViewScope === TeacherViewScopes.YEAR && (
              <TableHead>№</TableHead>
            )}
            {viewMode === ViewModes.BY_TEACHER &&
              (teacherViewScope === TeacherViewScopes.DAY ||
               teacherViewScope === TeacherViewScopes.MONTH ||
               teacherViewScope === TeacherViewScopes.YEAR) && (
                <TableHead>Дата</TableHead>
            )}
            <TableHead>Час</TableHead>
            {viewMode !== ViewModes.BY_GROUP && <TableHead>Група</TableHead>}
            <TableHead>Предмет</TableHead>
            {viewMode !== ViewModes.BY_TEACHER && <TableHead>Викладач</TableHead>}
            <TableHead>
              <span className="hidden sm:inline">Аудиторія</span>
              <span className="inline sm:hidden">Ауд.</span>
            </TableHead>
          </TableRow>
          </TableHeader>

          <TableBody>{rowsForRender}</TableBody>
        </Table>
      </LoaderWrapper>
    </div>
  );
};

export default ScheduleTableDisplay;
